import {
  BottomSheet,
  Button,
  Icon,
  IconButton,
  Loader,
  Modal,
  Text,
} from 'components'

import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { joiResolver } from '@hookform/resolvers/joi'
import { useForm } from 'react-hook-form'

import { TextField } from 'uikit'
import { useTenant, useToast } from 'shared/hooks'

import { creditCardSchema } from './schemas'
import { CreditCardForm } from './types'
import { brands } from './constants'

import {
  creditCardNumberMasker,
  expiryDateMasker,
  formatTimestampFromExpiryDate,
  getCreditCardNumberMaxLengthWithMask,
} from './utils'

import { usePostPaymentMethod } from 'shared/hooks/services/account'
import { usePutPaymentMethod } from 'shared/hooks/services/account/usePutPaymentMethod'

import { PaymentMethodRequest } from 'services/account'
import { CVV, GoBack } from '../Modals'

import styles from './styles.module.scss'

const CreditCard = () => {
  const { tenant } = useTenant()
  const { accountId } = useParams()

  const location = useLocation()
  const navigate = useNavigate()

  const { addToast } = useToast()

  const {
    setValue,
    watch,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CreditCardForm>({
    mode: 'onChange',
    resolver: joiResolver(creditCardSchema),
    defaultValues: {
      paymentMethodType: 'CREDIT_CARD',
      accountId,
      number: '',
      brand: {
        name: '',
      },
      securityCode: '',
      holderName: '',
      expiryDate: '',
    },
  })

  const isEditing = location.pathname.endsWith('/edit')

  const { brand } = watch()

  const { mutate: saveMutation, isLoading: saveLoading } =
    usePostPaymentMethod()
  const { mutate: editMutation, isLoading: editLoading } = usePutPaymentMethod()

  const onSubmit = (form: CreditCardForm) => {
    const request: PaymentMethodRequest = {
      paymentMethodType: form.paymentMethodType,
      cardBrandName: form.brand.id,
      cardNumber: form.number.replace(/\D/g, ''),
      cardExpiryDate: formatTimestampFromExpiryDate(form.expiryDate),
      cardSecurityCode: form.securityCode,
      cardHolderName: form.holderName,
      accountId: form.accountId,
    }

    const mutate = isEditing ? editMutation : saveMutation

    const successMsg = `Forma de pagamento ${isEditing ? 'editada' : 'cadastrada'} com sucesso.`
    const errorMsg = `Não foi possível ${isEditing ? 'editar' : 'cadastrar'} a forma de pagamento. Tente novamente.`

    mutate(request, {
      onSuccess: () => {
        navigate(-1)
        addToast({ message: successMsg, isSnackBar: true })
      },
      onError: () =>
        addToast({ message: errorMsg, type: 'alert', isSnackBar: true }),
    })
  }

  if (saveLoading || editLoading) return <Loader appearance="screen" />

  return (
    <>
      <div className={styles.container}>
        <div>
          <header className={styles.header}>
            <Modal.Root>
              <Modal.Trigger>
                <IconButton className={styles.navigation}>
                  <Icon
                    width={20}
                    name="chevron-sm-left"
                    color="element-default"
                  />
                </IconButton>
              </Modal.Trigger>

              <GoBack />
            </Modal.Root>

            <Icon
              url={tenant?.brand?.images?.horizontalLogo}
              width={151}
              height={28}
            />
          </header>

          <div className={styles.wrapper}>
            <Text weight="semibold" size="3xl" color="element-bold">
              Cartão de crédito
            </Text>

            <div className={styles.formContent}>
              <div>
                <div className={styles.optionContainer}>
                  <BottomSheet.Root>
                    <BottomSheet.Trigger>
                      <div className={styles.selected}>
                        <div>
                          <Text
                            size="sm"
                            weight="medium"
                            color="element-medium"
                            as="label"
                          >
                            Bandeira
                          </Text>
                          <Text
                            size="md"
                            weight="regular"
                            color="element-default"
                            {...register('brand.name')}
                          >
                            {watch('brand.name') || 'Selecione uma opção'}
                          </Text>
                        </div>

                        <IconButton className={styles.option}>
                          {!!brand?.icon && (
                            <Icon width={24} name={brand.icon} />
                          )}
                          <Icon
                            width={17}
                            name="edit"
                            color="element-default"
                          />
                          {!brand?.name && <div className={styles.bullet} />}
                        </IconButton>
                      </div>
                    </BottomSheet.Trigger>

                    <BottomSheet.Content>
                      <BottomSheet.Title>Selecione uma opção</BottomSheet.Title>

                      {brands?.map((brand) => (
                        <BottomSheet.Option
                          key={brand.id}
                          onClick={() => {
                            setValue('brand', brand, { shouldValidate: true })
                          }}
                        >
                          <div className={styles.option}>
                            <Icon
                              width={24}
                              name={brand.icon}
                              color="element-bold"
                            />
                            <Text size="md">{brand.name}</Text>
                          </div>
                        </BottomSheet.Option>
                      ))}
                    </BottomSheet.Content>
                  </BottomSheet.Root>
                </div>
              </div>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Text weight="medium" size="lg">
                  Dados do cartão
                </Text>

                <div className={styles.form}>
                  <div className={styles.field}>
                    <TextField
                      {...register('number')}
                      inputMode="numeric"
                      label="Número do cartão"
                      value={creditCardNumberMasker(
                        watch('number'),
                        watch('brand.name'),
                      )}
                      maxLength={getCreditCardNumberMaxLengthWithMask(
                        watch('brand.name'),
                      )}
                      message={errors.number?.message}
                    />
                  </div>

                  <div className={styles.field}>
                    <TextField
                      {...register('expiryDate')}
                      label="Data de validade"
                      value={expiryDateMasker(watch('expiryDate'))}
                      inputMode="numeric"
                      message={errors.expiryDate?.message}
                    />

                    <div className={styles.cvv}>
                      <TextField
                        {...register('securityCode')}
                        label="CVV"
                        type="number"
                        inputMode="numeric"
                        message={errors.securityCode?.message}
                      />

                      <Modal.Root>
                        <Modal.Trigger>
                          <IconButton>
                            <Icon
                              name="question"
                              width={12}
                              color={
                                errors.securityCode
                                  ? 'accent-red-default'
                                  : 'element-default'
                              }
                            />
                          </IconButton>
                        </Modal.Trigger>

                        <CVV />
                      </Modal.Root>
                    </div>
                  </div>

                  <div className={styles.field}>
                    <TextField
                      {...register('holderName')}
                      label="Nome exibido no cartão"
                      message={errors.holderName?.message}
                    />
                  </div>
                </div>

                <Button
                  className={styles.submit}
                  type="submit"
                  appearance="primary"
                  disabled={!isValid}
                >
                  Salvar
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default CreditCard
